// FLAGS: legacy
// Hostname-as-language: each of the five deployment hostnames serves exactly one locale, so a hostname
// answers two questions at once — "is this a configured language host" and "which language is it". The
// path-prefix scope (OI-25226) retires this mapping as the language source; until then it is the only one.
//
// This module owns the *client* half only. The server half is `OpenUp.Web/web.config:28-121`: ten IIS
// rewrite rules keyed on the same five `REACT_APP_*_DOMAIN` variables, which also carry the translated
// change-language segment per locale. Retiring this module does not retire those rules.
import { env } from "@src/env";
import {
  LanguageCodeDutchNl,
  LanguageCodeEnglishGb,
  LanguageCodeGermanDe,
  LanguageCodeFrenchFr,
  LanguageCodeSpanishEs,
} from "@src/utils/languageHelpers";

// Keyed by the env-configured hostname for each locale. Every environment sets all five
// (`OpenUp.Web/environments/*/variables.json`), so on a deployed host the lookup always hits; on
// localhost none of them match and every function here reports "not a language host".
const domainMapping: Record<string, string> = {
  [env.REACT_APP_DUTCH_DOMAIN]: LanguageCodeDutchNl,
  [env.REACT_APP_ENGLISH_DOMAIN]: LanguageCodeEnglishGb,
  [env.REACT_APP_GERMAN_DOMAIN]: LanguageCodeGermanDe,
  [env.REACT_APP_FRENCH_DOMAIN]: LanguageCodeFrenchFr,
  [env.REACT_APP_SPANISH_DOMAIN]: LanguageCodeSpanishEs,
};

// The language this hostname serves, or `undefined` when it serves none.
export const getLanguageForHost = (
  hostname: string = window.location.hostname,
): string | undefined => domainMapping[hostname];

// Whether this hostname is one of the five configured language hosts. Note what this does *not* say:
// it is not "am I in production" — it is true on every deployed environment, including feature envs —
// and after the apex collapse (OI-25229) it will mean "is this the apex".
export const isLanguageHost = (hostname?: string): boolean =>
  getLanguageForHost(hostname) !== undefined;

// The reverse lookup: the bare hostname serving a language (no scheme — callers add `https://`).
export const getHostForLanguage = (language: string): string | undefined =>
  Object.keys(domainMapping).find((host) => domainMapping[host] === language);
